import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingBag, User, ShieldCheck, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { cn } from '../../lib/utils';
import Button from '../ui/Button';

const navLinks = [
  { to: '/shop', label: 'Boutique' },
  { to: '/about', label: 'À propos' },
  { to: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const { isAuthenticated, isAdmin, user, signOut } = useAuth();
  const { itemCount, openCart } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const handleToggle = () => setIsOpen((prev) => !prev);
  const handleClose = () => setIsOpen(false);

  return (
    <header className="sticky top-0 z-40 border-b border-leather-200/70 bg-cream-50/80 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
        <Link
          to="/"
          className="flex items-center gap-2 font-display text-xl font-semibold tracking-wide text-leather-900"
          onClick={handleClose}
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-leather-900 text-cream-50 text-sm font-bold">
            ST
          </span>
          Sellerie Thaïs
        </Link>

        <nav className="hidden items-center gap-6 text-sm font-medium text-leather-700 md:flex">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                cn(
                  'transition-colors hover:text-leather-900',
                  isActive && 'text-leather-900'
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          {isAdmin && (
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 rounded-full border border-leather-300 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-leather-700 transition hover:border-leather-400"
            >
              <ShieldCheck className="h-4 w-4" />
              Administration
            </Link>
          )}
          <button
            type="button"
            onClick={openCart}
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-leather-200 bg-cream-100 text-leather-700 transition hover:border-leather-400"
            aria-label="Ouvrir le panier"
          >
            <ShoppingBag className="h-5 w-5" />
            {itemCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-gold-500 text-xs font-bold text-leather-900">
                {itemCount}
              </span>
            )}
          </button>
          {isAuthenticated ? (
            <div className="flex items-center gap-2">
              <Link
                to="/account"
                className="inline-flex items-center gap-2 rounded-full border border-leather-200 px-3 py-2 text-sm font-medium text-leather-700 transition hover:border-leather-400"
              >
                <User className="h-4 w-4" />
                {user?.full_name || 'Compte'}
              </Link>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={signOut}
                leftIcon={<LogOut className="h-4 w-4" />}
              >
                Se déconnecter
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 rounded-full border border-leather-200 px-3 py-2 text-sm font-medium text-leather-700 transition hover:border-leather-400"
              >
                <User className="h-4 w-4" />
                Se connecter
              </Link>
              <Button type="button" onClick={() => navigate('/register')}>
                Créer un compte
              </Button>
            </div>
          )}
        </div>

        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-leather-200 text-leather-700 transition hover:border-leather-400 md:hidden"
          onClick={handleToggle}
          aria-label="Ouvrir le menu"
        >
          {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {isOpen && (
        <div className="border-t border-leather-200/70 bg-cream-50 px-4 py-4 md:hidden">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 text-sm font-medium text-leather-700">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={handleClose}
                  className={({ isActive }) =>
                    cn(
                      'transition-colors hover:text-leather-900',
                      isActive && 'text-leather-900'
                    )
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </div>

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  openCart();
                  handleClose();
                }}
                className="inline-flex items-center justify-between rounded-xl border border-leather-200 px-4 py-3 text-sm font-semibold text-leather-700"
              >
                Panier
                <span className="rounded-full bg-leather-900 px-2 py-1 text-xs text-cream-50">
                  {itemCount}
                </span>
              </button>
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={handleClose}
                  className="inline-flex items-center gap-2 rounded-xl border border-leather-200 px-4 py-3 text-sm font-semibold text-leather-700"
                >
                  <ShieldCheck className="h-4 w-4" />
                  Administration
                </Link>
              )}
              {isAuthenticated ? (
                <>
                  <Link
                    to="/account"
                    onClick={handleClose}
                    className="inline-flex items-center gap-2 rounded-xl border border-leather-200 px-4 py-3 text-sm font-semibold text-leather-700"
                  >
                    <User className="h-4 w-4" />
                    Compte
                  </Link>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      signOut();
                      handleClose();
                    }}
                    leftIcon={<LogOut className="h-4 w-4" />}
                  >
                    Se déconnecter
                  </Button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={handleClose}
                    className="inline-flex items-center gap-2 rounded-xl border border-leather-200 px-4 py-3 text-sm font-semibold text-leather-700"
                  >
                    <User className="h-4 w-4" />
                    Se connecter
                  </Link>
                  <Button
                    type="button"
                    onClick={() => {
                      navigate('/register');
                      handleClose();
                    }}
                  >
                    Créer un compte
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
